import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Link2, Loader2, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Button } from '@gokrew/ui';
import { ComboBox } from '@gokrew/ui/components';
import { TeamsLogo } from '@gokrew/ui/components/TeamsLogo';
import { TranslationNS } from '@gokrew/models/constants';
import { teamsPopupReturnUrl } from '@gokrew/ui/utility/teamsPopupReturn';
import { linkTeamsTenant } from '@/api/teams-bot';
import { useAuth } from '@/context/AuthContext';
import { TEAMS_TOKEN_KEY } from './teams-token-key';
import styles from './TeamsConnectPage.module.css';
import { companyDisplayName } from '@gokrew/ui/utility/companyDisplayName';

type Status = 'ready' | 'linking' | 'success' | 'error' | 'missing';

/**
 * Lands here from the "Connect KollabHR" card the bot DMs in Teams. Unlike the
 * account-link pages this does NOT fire on mount: binding a whole tenant to a
 * company is a decision, so the admin confirms which company first.
 */
export default function TeamsConnectPage() {
  const { t } = useTranslation(TranslationNS.teams);
  const navigate = useNavigate();
  const { companyId, companies, companyName } = useAuth();

  const [status, setStatus] = useState<Status>('ready');
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    string | undefined
  >(companyId ?? undefined);
  // Read on first render: the effect below strips the query along with the
  // fragment token.
  const [popupReturnUrl] = useState(teamsPopupReturnUrl);

  useEffect(() => {
    // Stash the fragment token so it survives the login redirect — this link is
    // opened from the Teams desktop client, often in a logged-out browser.
    const match = window.location.hash.match(/[#&]token=([^&]*)/);
    if (match) {
      sessionStorage.setItem(TEAMS_TOKEN_KEY, decodeURIComponent(match[1]));
      window.history.replaceState(null, '', window.location.pathname);
    }
    if (!sessionStorage.getItem(TEAMS_TOKEN_KEY)) {
      setStatus('missing');
    }
  }, []);

  useEffect(() => {
    if (!selectedCompanyId && companyId) setSelectedCompanyId(companyId);
  }, [companyId, selectedCompanyId]);

  const companyOptions = useMemo(
    () =>
      (companies ?? []).map((company) => ({
        value: company.id,
        label: companyDisplayName(company),
      })),
    [companies]
  );

  const handleConnect = async () => {
    const teamsToken = sessionStorage.getItem(TEAMS_TOKEN_KEY);
    if (!teamsToken || !selectedCompanyId) {
      setStatus('missing');
      return;
    }

    setStatus('linking');
    try {
      await linkTeamsTenant(selectedCompanyId, teamsToken);
      sessionStorage.removeItem(TEAMS_TOKEN_KEY);
      if (popupReturnUrl) {
        window.location.replace(popupReturnUrl);
        return;
      }
      toast.success(t('teamsConnect.toast.success'));
      setStatus('success');
    } catch (err) {
      // The token is kept: the backend only spends it on a bind that actually
      // succeeded, so a transient failure is retryable from this same screen.
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('teamsConnect.error.fallback');
      toast.error(message);
      setErrorMsg(message);
      setStatus('error');
    }
  };

  const goToDashboard = () => navigate('/dashboard');

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <div className={styles.logoRow}>
          <TeamsLogo size={45} />
          <span className={styles.divider} />
          <div className={styles.companyBadge}>
            {companyName?.[0]?.toUpperCase() ?? 'K'}
          </div>
        </div>

        {status === 'ready' && (
          <>
            <div>
              <p className={styles.title}>{t('teamsConnect.ready.title')}</p>
              <p className={styles.description}>
                {t('teamsConnect.ready.description')}
              </p>
            </div>

            {companyOptions.length > 1 && (
              <div className={styles.field}>
                <label className={styles.label}>
                  {t('teamsConnect.ready.company_label')}
                </label>
                <ComboBox
                  options={companyOptions}
                  value={selectedCompanyId}
                  onChange={setSelectedCompanyId}
                  placeholder={t('teamsConnect.ready.company_placeholder')}
                />
              </div>
            )}

            <div className={styles.actions}>
              <Button
                variant="outline"
                type="button"
                onClick={() => void handleConnect()}
                disabled={!selectedCompanyId}
                className={styles.primaryBtn}
              >
                <Link2 size={14} />
                {t('teamsConnect.ready.confirm')}
              </Button>
            </div>
          </>
        )}

        {status === 'linking' && (
          <>
            <Loader2 size={40} className={styles.spinner} />
            <p className={styles.title}>{t('teamsConnect.linking.title')}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className={styles.iconOk}>
              <CheckCircle2 size={32} />
            </div>
            <div>
              <p className={styles.title}>{t('teamsConnect.success.title')}</p>
              <p className={styles.description}>
                {t('teamsConnect.success.description')}
              </p>
            </div>
            <Button
              variant="outline"
              type="button"
              onClick={goToDashboard}
              className={styles.primaryBtn}
            >
              {t('teamsConnect.success.go_to_dashboard')}
            </Button>
          </>
        )}

        {(status === 'error' || status === 'missing') && (
          <>
            <div className={styles.iconBad}>
              <XCircle size={32} />
            </div>
            <div>
              <p className={styles.title}>
                {status === 'error'
                  ? t('teamsConnect.error.title')
                  : t('teamsConnect.missing.title')}
              </p>
              <p className={styles.description}>
                {status === 'error'
                  ? errorMsg
                  : t('teamsConnect.missing.description')}
              </p>
            </div>
            <div className={styles.actions}>
              {/* Only the error path can retry — a missing token has nothing
                  to retry with. */}
              {status === 'error' && (
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => void handleConnect()}
                  className={styles.primaryBtn}
                >
                  <Link2 size={14} />
                  {t('teamsConnect.error.retry')}
                </Button>
              )}
              <Button
                variant="outline"
                type="button"
                onClick={goToDashboard}
                className={styles.primaryBtn}
              >
                {t('teamsConnect.error.back')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
